import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "antd";
import "./StatisticsCards.css";
import { Contract, ethers, JsonRpcProvider } from "ethers";
import { contract_address, getChainInfoById } from "../constants/const";
import {
  BrevisMarket_Contract_ABI,
  StakingController_Contract_ABI,
  StakingViewer_Contract_ABI,
} from "../constants/abi";
import { formatDecimalWithSeparation } from "celer-web-utils/lib/format";
import { useModalStore } from "@/store/modle";
import { tokenSymbol } from "./modals/StakeModal";
import { Skeleton } from "antd";

const StatisticsCards: React.FC = () => {
  const dashboardReload = useModalStore((state) => state.dashboardReload);

  const [totalProvers, setTotalProvers] = useState<number>(0);
  const [totalVaultAssets, setTotalVaultAssets] = useState<string>("");

  const [proofsGenerated, setProofsGenerated] = useState<number>(0);
  const [feeReceived, setFeeReceived] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    const provider = new JsonRpcProvider(
      getChainInfoById(
        Number(process.env.REACT_APP_PUBLIC_CLAIM_CHAIN || 59144)
      ).rpcUrl
    );

    const staking_controller_contract = new Contract(
      contract_address.staking_controller || "",
      StakingController_Contract_ABI,
      provider
    );

    const staking_viewer_contract = new Contract(
      contract_address.staking_viewer || "",
      StakingViewer_Contract_ABI,
      provider
    );

    const brevis_market_contract = new Contract(
      contract_address.brevis_market || "",
      BrevisMarket_Contract_ABI,
      provider
    );

    Promise.all([
      staking_controller_contract.stakingToken(),
      staking_viewer_contract.getSystemOverview(),
      brevis_market_contract.getGlobalStatsTotal(),
    ]).then(async (res: any) => {
      const ERC20_ABI = [
        "function symbol() view returns (string)",
        "function decimals() view returns (uint8)",
      ];
      const tokenAddress = res[0];
      const token = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      const [symbol, decimals] = await Promise.all([
        token.symbol(),
        token.decimals(),
      ]);
      // const icon = `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${tokenAddress}/logo.png`;
      // console.log("symbol icon:", icon);

      localStorage.setItem("symbol", symbol);
      localStorage.setItem("decimals", decimals);
      localStorage.setItem("tokenAddress", tokenAddress);

      // console.log("symbol:", symbol);
      // console.log("decimals:", decimals);

      let totalProvers = Number(res[1].totalProvers || 0);
      setTotalProvers(totalProvers);

      setTotalVaultAssets(
        formatDecimalWithSeparation(
          res[1].totalVaultAssets?.toString(),
          0,
          decimals,
          "floor",
          ",",
          true
        )
      );

      setProofsGenerated(Number(res[2].totalFulfilled || 0));
      setFeeReceived(
        formatDecimalWithSeparation(
          res[2].totalFees?.toString(),
          2,
          decimals,
          "floor",
          " ,",
          true
        )
      );
      setLoading(false);
    });
  }, [dashboardReload]);

  return (
    <div className="statistics-section">
      <Row gutter={[20, 12]}>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card stat-card1">
            <div className="stat-card-content">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                  }}
                >
                  <div style={{ width: 100 }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 100 }}
                      title={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="stat_number">
                  {totalProvers.toLocaleString()}{" "}
                  <span style={{ fontSize: 20 }}> </span>
                </div>
              )}
              <div className="stat_des">Number of Provers</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card stat-card1">
            <div className="stat-card-content">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                  }}
                >
                  <div style={{ width: 200 }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 200 }}
                      title={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="stat_number">
                  {totalVaultAssets}{" "}
                  <span style={{ fontSize: 20 }}>{tokenSymbol}</span>
                </div>
              )}
              <div className="stat_des">Total Staked</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card stat-card2">
            <div className="stat-card-content">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                  }}
                >
                  <div style={{ width: 100 }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 100 }}
                      title={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="stat_number">
                  {proofsGenerated.toLocaleString()}{" "}
                  <span style={{ fontSize: 20 }}> </span>
                </div>
              )}
              <div className="stat_des">Total Number of Proofs Generated</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card stat-card3">
            <div className="stat-card-content">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                  }}
                >
                  <div style={{ width: 100 }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 1, width: 100 }}
                      title={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="stat_number">
                  {feeReceived}{" "}
                  <span style={{ fontSize: 20 }}>{tokenSymbol}</span>
                </div>
              )}
              <div className="stat_des">Total Proof Fee Received</div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StatisticsCards;
